import React, { useMemo } from 'react';
import block from 'bem-cn';
import { shallowEqual, useSelector } from 'react-redux';
import arrowUpSVG from 'features/creativeFunctionality/img/arrow-up.svg';
import arrowDownSVG from 'features/creativeFunctionality/img/arrow-down.svg';
import { useCustomProvidersModel } from 'features/creativeFunctionality/models';
import ProviderIcon from 'components/ProviderIcon';
import Switcher from 'components/Switcher';
import SVG from 'components/SVG';
import Button from 'components/Button';
import './ProvidersCustomBlock.scss';
import DragAndDrop from 'components/DragAndDrop';

const b = block('provider-custom-block');

const ProvidersCustomBlock = () => {
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const { input, output } = useCustomProvidersModel();

  const GameTypesElements = useMemo(() => {
    return input.activeGameTypes.map(gp => (
      <div
        key={gp.value}
        onClick={() => input.handleActiveGameTypes(gp)}
        className={b('game-type', { active: gp.isActive })}>
        {locale.gameTypes[gp.name] ?? gp.name}
      </div>
    ));
  }, [input.activeGameTypes, input.handleActiveGameTypes, locale]);

  const ProvidersElements = useMemo(
    () =>
      input.activeProviders
        .filter(p => p.type === output.activeGameType.value)
        .map((p, idx, arr) => (
          <div key={p.value} className={b('provider', { disabled: p.disabled })}>
            <div className={b('wrapper')}>
              <div className={b('provider-game-type')}>{locale.gameTypes[output.activeGameType.name]}</div>
              <div className={b('provider-item')}>
                <ProviderIcon className={b('provider-item-icon')} name={p.value.toLowerCase()} />
                <span className={b('provider-item-name')}>{p.name}</span>
                {p.disabled && <span className={b('provider-locked')}>{locale.locked || 'Locked'}</span>}
              </div>
            </div>
            <div className={b('wrapper')}>
              <div className={b('provider-switcher')}>
                <Switcher
                  color="purple"
                  isActive={p.isActive}
                  disabled={p.disabled}
                  onSwitch={() => input.handleActiveProviders(p)}
                />
              </div>
              <div className={b('provider-controller')}>
                <SVG
                  svgProps={{ svg: arrowUpSVG }}
                  onClick={() => input.changeProviderOrder(p, 1)}
                  className={b('provider-arrow', { disabled: idx === 0 || p.disabled })}
                />
                <SVG
                  svgProps={{ svg: arrowDownSVG }}
                  onClick={() => input.changeProviderOrder(p, 0)}
                  className={b('provider-arrow', { disabled: idx === arr.length - 1 || p.disabled })}
                />
              </div>
            </div>
          </div>
        )),
    [input.activeProviders, input.handleActiveProviders, input.changeProviderOrder, output.activeGameType, locale],
  );

  return (
    <div className={b()}>
      <div className={b('header')}>
        <h1 className={b('header-title')}>{locale.providers}</h1>
        <span className={b('header-total')}>
          {locale.totalProviders}: {input.activeProviders.length}
        </span>
      </div>
      <div className={b('game-types')}>{GameTypesElements}</div>
      <div className={b('content')}>
        <div className={b('providers')}>
          <DragAndDrop
            callback={({ startIndex, endIndex }) =>
              input.changeProviderOrderByDragging({
                startIndex,
                endIndex,
                type: output.activeGameType.value,
              })
            }>
            {ProvidersElements}
          </DragAndDrop>
        </div>
        <div className={b('buttons')}>
          <div className={b('button')}>
            <Button
              disabled={output.returnBackDisabled}
              color="black-transparent"
              text={locale.returnBack}
              callBack={input.handleReturnBack}
            />
          </div>
          <div className={b('button')}>
            <Button text={locale.sendToConfirm} callBack={input.handleSendConfirm} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProvidersCustomBlock;
